/**
 * Tons gerados com WebAudio (sem arquivos binários): toque de chamada recebida, ringback de
 * chamada efetuada (cadência brasileira 425 Hz 1s/4s), bipes de login/logout/falha e DTMF local.
 */

type ToneHandle = { stop: () => void };

let ctx: AudioContext | null = null;
let sinkId = "";

function audioCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  if (!ctx) {
    ctx = new AC();
    void applySink();
  }
  if (ctx.state === "suspended") void ctx.resume().catch(() => {});
  return ctx;
}

async function applySink() {
  const c = ctx as (AudioContext & { setSinkId?: (id: string) => Promise<void> }) | null;
  if (!c?.setSinkId) return;
  try {
    await c.setSinkId(sinkId);
  } catch {
    /* dispositivo removido / sem permissão: segue no padrão */
  }
}

/** Dispositivo de saída do toque (vazio = padrão do sistema). */
export function setRingerSink(id: string) {
  sinkId = id;
  void applySink();
}

/** Destrava o AudioContext num gesto do usuário (política de autoplay). */
export function unlockAudio() {
  audioCtx();
}

function beepAt(c: AudioContext, freqs: number[], start: number, duration: number, gain = 0.12) {
  const g = c.createGain();
  g.gain.setValueAtTime(0, start);
  g.gain.linearRampToValueAtTime(gain, start + 0.015);
  g.gain.setValueAtTime(gain, start + duration - 0.02);
  g.gain.linearRampToValueAtTime(0, start + duration);
  g.connect(c.destination);
  for (const f of freqs) {
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.value = f;
    o.connect(g);
    o.start(start);
    o.stop(start + duration + 0.01);
  }
}

function loop(pattern: { freqs: number[]; on: number; off: number; gain: number; bursts?: number }): ToneHandle {
  const c = audioCtx();
  if (!c) return { stop: () => {} };
  let stopped = false;
  const bursts = pattern.bursts ?? 1;
  const cycle = () => {
    if (stopped) return;
    const t0 = c.currentTime + 0.02;
    for (let i = 0; i < bursts; i++) {
      beepAt(c, pattern.freqs, t0 + i * (pattern.on + 0.2), pattern.on, pattern.gain);
    }
  };
  cycle();
  const period = (bursts * (pattern.on + 0.2) + pattern.off) * 1000;
  const id = window.setInterval(cycle, period);
  return {
    stop: () => {
      stopped = true;
      window.clearInterval(id);
    },
  };
}

let ringing: ToneHandle | null = null;
let ringback: ToneHandle | null = null;

export function startRingtone() {
  stopRingtone();
  ringing = loop({ freqs: [440, 480], on: 0.4, off: 2.2, gain: 0.16, bursts: 2 });
}

export function stopRingtone() {
  ringing?.stop();
  ringing = null;
}

export function startRingback() {
  stopRingback();
  ringback = loop({ freqs: [425], on: 1, off: 4, gain: 0.08 });
}

export function stopRingback() {
  ringback?.stop();
  ringback = null;
}

export function stopAllTones() {
  stopRingtone();
  stopRingback();
}

export function playCue(kind: "login" | "logout" | "fail" | "ended") {
  const c = audioCtx();
  if (!c) return;
  const t = c.currentTime + 0.02;
  switch (kind) {
    case "login":
      beepAt(c, [660], t, 0.09);
      beepAt(c, [880], t + 0.12, 0.12);
      break;
    case "logout":
      beepAt(c, [880], t, 0.09);
      beepAt(c, [660], t + 0.12, 0.12);
      break;
    case "ended":
      beepAt(c, [480, 620], t, 0.18, 0.08);
      break;
    case "fail":
      beepAt(c, [300], t, 0.25, 0.1);
      beepAt(c, [300], t + 0.35, 0.25, 0.1);
      break;
  }
}

const DTMF_FREQS: Record<string, [number, number]> = {
  "1": [697, 1209],
  "2": [697, 1336],
  "3": [697, 1477],
  "4": [770, 1209],
  "5": [770, 1336],
  "6": [770, 1477],
  "7": [852, 1209],
  "8": [852, 1336],
  "9": [852, 1477],
  "*": [941, 1209],
  "0": [941, 1336],
  "#": [941, 1477],
};

/** Retorno audível local da tecla (o DTMF real vai por RFC 4733). */
export function playDtmfFeedback(key: string) {
  const f = DTMF_FREQS[key];
  const c = audioCtx();
  if (!f || !c) return;
  beepAt(c, f, c.currentTime + 0.01, 0.09, 0.06);
}
